import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import { FormField } from '../molecues/FormField';
import type { DramaCast } from '../../types/api/drama';
import type { Cast } from '../../types/api/cast';

export interface CastAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: 'movie' | 'drama' | 'music-video';
  contentId: string;
  contentTitle: string;
  /**
   * Fetch currently assigned casts for this content.
   * Typically wired to useCastAssignment().getCasts().
   */
  fetchAssignedCasts: () => Promise<DramaCast[]>;
  /**
   * Search casts by keyword for typeahead.
   */
  searchCasts: (keyword: string) => Promise<Cast[]>;
  /**
   * Assign a cast with role.
   */
  onAssign: (castId: string, role: string) => Promise<void>;
  /**
   * Remove a cast.
   */
  onRemove: (castId: string) => Promise<void>;
  /**
   * Update role of an assigned cast.
   */
  onUpdateRole: (castId: string, role: string) => Promise<void>;
}

export const CastAssignmentModal: React.FC<CastAssignmentModalProps> = ({
  isOpen,
  onClose,
  contentType,
  contentId: _contentId,
  contentTitle,
  fetchAssignedCasts,
  searchCasts,
  onAssign,
  onRemove,
  onUpdateRole,
}) => {
  const [assignedCasts, setAssignedCasts] = useState<DramaCast[]>([]);
  const [isLoadingAssigned, setIsLoadingAssigned] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<Cast[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const [selectedCastId, setSelectedCastId] = useState<string | null>(null);
  const [roleInput, setRoleInput] = useState('');

  const [updatingCastId, setUpdatingCastId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const contentLabel = useMemo(
    () => {
      if (contentType === 'movie') return 'Movie';
      if (contentType === 'music-video') return 'Music Video';
      return 'Series / Drama';
    },
    [contentType]
  );

  // Load assigned casts when modal opens
  useEffect(() => {
    const loadAssigned = async () => {
      if (!isOpen) return;
      setIsLoadingAssigned(true);
      setErrorMessage(null);
      try {
        const data = await fetchAssignedCasts();
        setAssignedCasts(data);
      } catch (error: any) {
        console.error('Failed to load assigned casts:', error);
        setErrorMessage(error?.message || 'Failed to load assigned casts.');
      } finally {
        setIsLoadingAssigned(false);
      }
    };

    void loadAssigned();
  }, [isOpen, fetchAssignedCasts]);

  // Reset internal state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchKeyword('');
      setSearchResults([]);
      setSelectedCastId(null);
      setRoleInput('');
      setErrorMessage(null);
      setUpdatingCastId(null);
    }
  }, [isOpen]);

  const handleSearch = async (keyword: string) => {
    setSearchKeyword(keyword);
    setErrorMessage(null);

    const trimmed = keyword.trim();
    if (!trimmed) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchCasts(trimmed);
      setSearchResults(results);
    } catch (error: any) {
      console.error('Failed to search casts:', error);
      setErrorMessage(error?.message || 'Failed to search casts.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedCastId) {
      setErrorMessage('Please select a cast to assign.');
      return;
    }
    if (!roleInput.trim()) {
      setErrorMessage('Role is required to assign a cast.');
      return;
    }

    setErrorMessage(null);
    setUpdatingCastId(selectedCastId);
    try {
      await onAssign(selectedCastId, roleInput.trim());
      const updatedAssigned = await fetchAssignedCasts();
      setAssignedCasts(updatedAssigned);
      setSelectedCastId(null);
      setRoleInput('');
      setSearchKeyword('');
      setSearchResults([]);
    } catch (error: any) {
      console.error('Failed to assign cast:', error);
      setErrorMessage(error?.message || 'Failed to assign cast.');
    } finally {
      setUpdatingCastId(null);
    }
  };

  const handleRemove = async (castId: string) => {
    setErrorMessage(null);
    setUpdatingCastId(castId);
    try {
      await onRemove(castId);
      setAssignedCasts((prev) => prev.filter((c) => c.castId !== castId));
    } catch (error: any) {
      console.error('Failed to remove cast:', error);
      setErrorMessage(error?.message || 'Failed to remove cast.');
    } finally {
      setUpdatingCastId(null);
    }
  };

  const handleInlineRoleUpdate = async (castId: string, role: string) => {
    if (!role.trim()) {
      setErrorMessage('Role cannot be empty.');
      return;
    }

    setErrorMessage(null);
    setUpdatingCastId(castId);
    try {
      await onUpdateRole(castId, role.trim());
      const updatedAssigned = await fetchAssignedCasts();
      setAssignedCasts(updatedAssigned);
    } catch (error: any) {
      console.error('Failed to update cast role:', error);
      setErrorMessage(error?.message || 'Failed to update cast role.');
    } finally {
      setUpdatingCastId(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Manage Casts • ${contentLabel}: ${contentTitle}`}
      size="xl"
    >
      <div className="space-y-6">
        {errorMessage && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 text-left">
            {errorMessage}
          </div>
        )}

        {/* Assigned Casts List */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-800 text-left">
            Assigned Casts
          </h3>
          {isLoadingAssigned ? (
            <p className="text-sm text-gray-500 text-left">Loading casts...</p>
          ) : assignedCasts.length === 0 ? (
            <p className="text-sm text-gray-500 text-left">
              No casts assigned yet. Use the search below to add casts.
            </p>
          ) : (
            <ul className="space-y-2">
              {assignedCasts.map((cast) => (
                <li
                  key={cast.castId}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {cast.profileImageUrl ? (
                      <img
                        src={cast.profileImageUrl}
                        alt={cast.name}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 flex-shrink-0">
                        NA
                      </div>
                    )}
                    <div className="min-w-0 text-left">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {cast.name}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <input
                          className="w-full max-w-xs rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308]/30"
                          value={cast.role || ''}
                          onChange={(e) => {
                            const nextRole = e.target.value;
                            setAssignedCasts((prev) =>
                              prev.map((c) =>
                                c.castId === cast.castId ? { ...c, role: nextRole } : c
                              )
                            );
                          }}
                          onBlur={(e) =>
                            handleInlineRoleUpdate(cast.castId, e.target.value)
                          }
                          placeholder="Role in this content (e.g., Lead Actor)"
                        />
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="danger"
                    onClick={() => handleRemove(cast.castId)}
                    isLoading={updatingCastId === cast.castId}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200" />

        {/* Search & Assign */}
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-800 text-left">
            Add Cast to {contentLabel}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Search Casts"
              value={searchKeyword}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by cast name..."
              helperText="Type at least 1 character to search"
            />

            <FormField
              label="Role in this Content"
              value={roleInput}
              onChange={(e) => setRoleInput(e.target.value)}
              placeholder="e.g., Lead Actor, Director"
            />
          </div>

          {isSearching && (
            <p className="text-xs text-gray-500 text-left">Searching casts...</p>
          )}

          {searchResults.length > 0 && (
            <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200">
              <ul className="divide-y divide-gray-100">
                {searchResults.map((cast) => {
                  const isSelected = selectedCastId === cast.castId;
                  const isAlreadyAssigned = assignedCasts.some(
                    (ac) => ac.castId === cast.castId
                  );

                  return (
                    <li
                      key={cast.castId}
                      className={`flex items-center justify-between gap-3 px-3 py-2 cursor-pointer ${
                        isSelected ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'
                      }`}
                      onClick={() =>
                        setSelectedCastId((prev) =>
                          prev === cast.castId ? null : cast.castId
                        )
                      }
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {cast.profileImageUrl ? (
                          <img
                            src={cast.profileImageUrl}
                            alt={cast.name}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-500 flex-shrink-0">
                            NA
                          </div>
                        )}
                        <div className="min-w-0 text-left">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {cast.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {cast.nationality || '—'} • {cast.role || 'Cast'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isAlreadyAssigned && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            Assigned
                          </span>
                        )}
                        <span
                          className={`text-[11px] font-medium ${
                            isSelected ? 'text-blue-600' : 'text-gray-400'
                          }`}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              variant="primary"
              onClick={handleAssign}
              isLoading={!!selectedCastId && updatingCastId === selectedCastId}
              disabled={!selectedCastId || !roleInput.trim()}
            >
              Assign Cast
            </Button>
          </div>
        </section>
      </div>
    </Modal>
  );
};


